// @Version: 1.0.4
import { Component, forwardRef, EventEmitter, Output, Input, OnInit, AfterContentInit, OnDestroy, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, FormControl, FormBuilder } from "@angular/forms";
import { UntilDestroy } from '@ngneat/until-destroy';
import { IFieldItem } from '../field-item.model';
import { TransformItemService } from './transform-item.service';
import { TRANSLATE_PATH } from 'src/app/shared/shared.model';
import { ErrorMessagesService } from 'src/app/shared/services/errorMessages/error-messages.service';
import { MatDialog } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { FieldItemDialoFileUploadComponent } from '../../../dialogs/fileupload/dialog-fileupload.component';
import { ResponsiveService } from 'src/app/shared/services/responsive/responsive.service';
import { IUploadFileData, IUploadFileMetaData } from 'src/app/shared/dialogs/fileupload/dialog-fileupload.model';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'app-field-item-fileupload-button',
    templateUrl: './fileupload-button.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FieldItemFileuploadButtonComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FieldItemFileuploadButtonComponent),
            multi: true
        },
        ErrorMessagesService
    ],
    standalone: false
})
export class FieldItemFileuploadButtonComponent implements OnChanges, ControlValueAccessor, Validator, OnDestroy {
   public TRANSLATE_PATH = TRANSLATE_PATH; 

   public fcFileuploadButton: FormControl = this.fb.control("");
   public fileUploaded: boolean = false;

   private fieldItem_: IFieldItem = {};
   private fileExtensions_: string[] = [];
   private sub_?: Subscription;
   private uploadColors_: string[] = [];
   private uploadIcons_: string[]= [];
   private metaData_: IUploadFileMetaData = {} as IUploadFileMetaData;
   public uploadFileData: IUploadFileData[] = []; 

   @Input()
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value;
   
   }
   get fieldItem (): IFieldItem {
      return this.fieldItem_;
   }
   
   @Output() isRequiredMarker = new EventEmitter<boolean>();

   get colorSuccess (): string {
      return this.uploadColors_[1];
   }

   get colorDefault (): string {
      return this.uploadColors_[0];
   }
   get iconSuccess (): string {
      return this.uploadIcons_[1];
   }

   get iconDefault (): string {
      return this.uploadIcons_[0];
   }

   get fileExtensions (): string[] {
      return this.fileExtensions_;
   }

   constructor (public sTransformItem: TransformItemService, private fb: FormBuilder, private dialog: MatDialog, public sResponsive: ResponsiveService, private overlay: Overlay, private cdRef: ChangeDetectorRef) {}

   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
      this.fileExtensions_ = this.sTransformItem.transform(changes.fieldItem.currentValue.attribute_2);
      this.uploadColors_ = this.sTransformItem.transform(changes.fieldItem.currentValue.attribute_1);
      this.uploadIcons_ = this.sTransformItem.transform(changes.fieldItem.currentValue.attribute_3);

      this.metaData_.allowedExtensions = this.fileExtensions;
      this.metaData_.translatePath = changes.fieldItem.currentValue.attribute_4 ?? "";
      this.metaData_.required = changes.fieldItem.currentValue.required ?? false; 
      if (this.sub_) {
         this.sub_.unsubscribe();
         this.sub_ = undefined;
      }
      
      this.fcFileuploadButton.setValue(this.formatContent(changes.fieldItem.currentValue.content));
      this.sub_ = this.fcFileuploadButton.valueChanges.subscribe(res => {
         if (res) this.onClickUpload();
      });  
      this.isRequiredMarker.emit(changes.fieldItem.currentValue.required);
      }
   }

   ngOnDestroy (): void {
      this.fcFileuploadButton = this.fb.control("");
      this.fieldItem_ = {};
      this.uploadColors_ = [];
      this.uploadIcons_ = [];
      this.fileUploaded = false;
      this.fileExtensions_ = []; 
      this.metaData_ = {} as IUploadFileMetaData;
      this.uploadFileData = []; 
   }

   validate (control: AbstractControl): ValidationErrors | null {
      this.fcFileuploadButton.updateValueAndValidity({emitEvent: false, onlySelf: true});
      return !this.fcFileuploadButton.dirty && this.fieldItem.required ? {noDownload: true} : null; 
   }

   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;
   }


   writeValue (input: boolean | string | undefined | null): void {
      const value = this.formatContent(input); 
      this.fcFileuploadButton.setValue(value);
      if (value) this.setDirtyTouched();
      this.onValidationChange();
   }

   private setDirtyTouched (): void {
      this.fcFileuploadButton.markAsDirty(); 
      this.fcFileuploadButton.markAsTouched();
   }

   private formatContent (input: boolean | string | undefined | null): boolean {
      return typeof input == "string" && input == "true" ? true : typeof input == 'boolean' ? input : false;
   }

   private onTouched () {}

   private onChange (_: IUploadFileData[]) {}

   registerOnChange (fn: any): void {     
      this.onChange = fn
   }

   registerOnTouched (fn: any): void { this.onTouched = fn; }

   onValidationChange: any = () => {}; 

   public onClickUpload (): void {
      const dialog = this.dialog.open(FieldItemDialoFileUploadComponent, {
         autoFocus: false,
         disableClose: true,
         backdropClass: ['backdrop-background-GD', 'as-page'],
         data: {metaData: this.metaData_, fileData: JSON.parse(JSON.stringify(this.uploadFileData)) },
         scrollStrategy: this.overlay.scrollStrategies.noop()
      });

      dialog.componentInstance.isFileUploaded.subscribe(value => {
         this.fileUploaded = value;
         this.cdRef.detectChanges(); 
      });

      dialog.afterClosed().subscribe((saveData: IUploadFileData[]) => {
         if (Array.isArray(saveData)) {
            this.onChange(saveData);
            this.uploadFileData = saveData; 
         }

         this.fcFileuploadButton.setValue("", {emitEvent: false});
      })
   }
}
